import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { onSocketEvent } from '../lib/socket'
import { useAuthStore } from '../stores/authStore'

/**
 * Mount once near the app root (inside `<QueryClientProvider>` so
 * `useQueryClient` resolves). Listens to the backend's `data:changed`
 * tenant broadcast and invalidates the matching React Query keys so
 * every open tab refetches in real time.
 *
 * The backend helper is `emitDataChanged` in `flowdesk-api/src/config/
 * socket.js`. Each payload carries `{ resource, op, id, actorId,
 * parentId, at }`. We map `resource` → top-level React Query key(s)
 * using `RESOURCE_KEYS`; React Query's prefix-matching means invalidating
 * `['tasks']` covers `['tasks', { listId, …}]` too.
 *
 * Self-emit deduplication: we skip invalidations whose `actorId`
 * matches the current user — the originating tab has already
 * optimistically updated via its mutation's onSuccess. This avoids a
 * redundant refetch on the tab that caused the change.
 */

// Map from backend `resource` slug to one or more React Query key
// PREFIXES. React Query prefix-matches, so `['tasks']` covers every
// query whose key starts with `['tasks', …]`. Add a row here when a
// new resource ships; no other code needs to change.
//
// The lists below include EVERY top-level key shape any page in the
// app uses for that resource — pages that use a singular form
// (`['space', id]`) need a separate entry from those that use the
// plural list (`['spaces']`). Both keep working because invalidating
// either prefix only affects matching queries.
const RESOURCE_KEYS = {
  // Files & Links — Files & Links page, storage card, folder card.
  // Each also bumps ['space-overview']: the Space Overview tab's KPI strip
  // has a "Files & Links" card counting File + ExternalLink rows in the
  // space, so a file / link added to (or removed from) a task must refresh
  // it or the card reads stale until a manual reload (QA).
  file: [['files'], ['space-overview']],
  'file-folder': [['file-folders'], ['files'], ['space-overview']], // folder rename / delete affects file counts too
  'external-link': [['external-links'], ['file-folders'], ['space-overview']],

  // Hierarchy — used across Spaces, Workspaces, Inbox, Dashboard
  workspace: [['workspaces']],
  // Space mutations cascade further than the space record itself:
  // - the workspace tree in the sidebar shows the space (so the
  //   workspaces query needs to refresh on create / rename / delete)
  // - on DELETE every child list / document / folder / member goes
  //   too, so their cached lists must be refetched or they'll show
  //   ghost children that 404 the moment the user clicks them
  // - other open tabs viewing the deleted space recover via
  //   SpacePage's local `spaceQuery.isError → navigate` effect, not
  //   via a global window.location.reload(). Reloading every tab on
  //   every space edit was destructive (in-progress comments,
  //   modals, and form state were all blown away when ANY other
  //   user did anything to ANY space).
  space: [
    ['space'],
    ['spaces'],
    ['workspaces'],
    ['lists'],
    ['list'],
    ['documents'],
    ['document'],
    ['folders'],
    ['space-members'],
  ],
  // ['space-overview'] → the Overview tab's "Lists" KPI + list breakdown.
  list: [['list'], ['lists'], ['space-overview']],
  // Space-level folder (NOT the Files & Links FileFolder — different
  // collection, different key). Move/delete also affects child lists
  // and documents, so bump those query trees too.
  folder: [['folders'], ['lists'], ['documents']],

  // Tasks + comments — used by:
  //   - Spaces page          → ['tasks', listId]
  //   - Task Monitor (admin) → ['admin', 'tasks', 'monitor', …]
  //   - My Tasks (employee)  → ['my-tasks', …]
  //   - Team Tasks roll-up   → ['team', 'tasks', …]
  //   - Legacy alias         → ['task-monitor']
  //   - Kanban Board         → ['board', listId]
  //   - Calendar             → ['calendar', from, to, scope]
  //   - Home dashboard       → ['workspace-overview'] (Team Supervision
  //                            tiles: Team Tasks Open / Completed This
  //                            Week …) + ['me-dashboard-overview']
  //                            (personal counts).
  // Board + Calendar were missing here, so a task created / moved /
  // edited / deleted by ANOTHER client never live-refreshed those two
  // surfaces — they only updated on a manual reload. The two dashboard
  // keys were likewise missing, so the Home dashboard's task counts only
  // changed after a manual page refresh.
  //
  // Gamification keys (`leaderboard` / `gamification-me` / `gamification`)
  // are here because task lifecycle changes MOVE POINTS: completing a task
  // awards them, deleting a completed task removes its ledger entries, and
  // reopening/rejecting reverses them. Without these, the Leaderboard and
  // Performance pages showed stale points until a manual reload — e.g. the
  // assignee's total didn't drop after a completed task was deleted.
  // (`gamification-settings` is deliberately NOT invalidated — it's the
  // points-rules config, unaffected by task changes, and its key doesn't
  // match the `['gamification']` prefix anyway.)
  task: [
    ['tasks'],
    ['task-monitor'],
    ['admin', 'tasks', 'monitor'],
    ['my-tasks'],
    // ['team'] not ['team','tasks'] — React Query prefix-matches, and the
    // Team Overview roster keys on ['team', period] ('weekly' / 'monthly').
    // ['team','tasks'] does NOT prefix-match that, so the roster's Open /
    // Overdue / Done columns sat up to a minute stale on its own 60s poll
    // while every other task surface updated instantly (QA 2026-09-12).
    // The broader prefix covers both team query families, which is right:
    // they read the same task data.
    ['team'],
    // [AI · Phase 4B] Advisory proof review — refetch when the async AI result
    // lands (runProofVerification emits a `task` change).
    ['task-proof-review'],
    ['board'],
    ['calendar'],
    ['workspace-overview'],
    ['me-dashboard-overview'],
    ['leaderboard'],
    ['gamification-me'],
    ['gamification'],
    // Performance page's points total lagged behind the leaderboard on a
    // task completed by ANOTHER client until a manual reload.
    ['performance'],
    // Space Overview KPI strip: Total / Open / Completed cards plus the
    // status & priority breakdowns. Without this a task created / completed
    // / deleted by ANOTHER client left those counts stale until reload.
    ['space-overview'],
  ],
  comment: [['task-comments']],

  // Reports — three distinct UI surfaces:
  //   - employee: `['reports', …]`, `['team-reports', …]`
  //   - admin:    `['admin-reports', …]`
  //   - schedule: `['report-schedule']`
  // Plus the two dashboards: the employee Home's Daily Report card
  // (submitted / pending / weekly / monthly prompts) and the manager
  // rollup's "reports this week" + missing-today tiles both read
  // report state, so a submit must refresh them live (QA: the card
  // kept showing "isn't submitted yet" after submitting).
  // Also covers Report Recipients: they're embedded on the schedule row and
  // saved through the schedule PATCH, which emits `work-report`, so a second
  // admin tab's ['report-schedule'] query refetches the updated recipients.
  'work-report': [
    ['reports'],
    ['team-reports'],
    ['admin-reports'],
    ['report-schedule'],
    ['me-dashboard-overview'],
    ['workspace-overview'],
  ],

  // Unified Goals (Weekly + Monthly) — the board is keyed ['goals', type,
  // period]; a prefix invalidation refreshes every period/tab + the analysis.
  // `['goal']` is added so an OPEN goal detail page (keyed ['goal', id]) also
  // refetches on the same event — e.g. a check-in posted by someone else shows
  // up live, without a manual browser refresh. (Same singular+plural pattern as
  // `document` above.)
  goal: [['goals'], ['goals-analysis'], ['goal']],

  // SOPs / Documents — documents use both singular and plural keys
  // because the editor mounts `['document', id]` while the list view
  // queries `['documents']`.
  sop: [['sops']],
  document: [['documents'], ['document']],

  // Support tickets — four separate query-key prefixes, one per page:
  //   ['support-tickets']        — Platform Admin list
  //   ['support-ticket']         — Platform Admin detail
  //   ['support-tickets-tenant'] — Company Admin list
  //   ['support-ticket-tenant']  — Company Admin detail
  // Both pages refresh on every ticket mutation regardless of which
  // side made the change. The backend `emitSupportTicketChanged`
  // helper fans the `data:changed` event out to BOTH the ticket's
  // tenant room (Company Admins) AND the platform room every Super
  // Admin joins — so neither audience misses an update.
  'support-ticket': [
    ['support-tickets'],
    ['support-ticket'],
    ['support-tickets-tenant'],
    ['support-ticket-tenant'],
  ],

  // Servexa Support Tickets & Feature Suggestions — the ACTIVE support flow
  // (spec A6/A7), distinct from the legacy `support-ticket` above. When a
  // Servexa status webhook applies a change (servexa.webhook.service) it emits
  // `resource:'servexa-ticket'|'servexa-feature'` to the raising user's tenant
  // room, so the list AND any open detail refetch live. Two key prefixes each:
  //   ['servexa-tickets'] / ['servexa-ticket', id]
  //   ['servexa-features'] / ['servexa-feature', id]
  'servexa-ticket': [['servexa-tickets'], ['servexa-ticket']],
  'servexa-feature': [['servexa-features'], ['servexa-feature']],

  // Membership — surfaced as multiple key shapes:
  //   ['members', workspaceId]       — Members tab on workspace
  //   ['space-members', spaceId]     — Spaces page right-panel
  //   ['workspace-members', wsId]    — Workspace Settings dialog
  //   ['teammates', wsId]            — Inbox teammate-picker
  //   ['list-members', listId]       — List Share dialog roster
  // list-members was missing, so adding/removing a member on a List
  // (which the backend broadcasts as resource:'member' from
  // list-members.service.js) never live-refreshed an open List Share
  // dialog — the roster only updated when the viewer reopened it.
  member: [
    ['members'],
    ['workspace-members'],
    ['space-members'],
    ['teammates'],
    ['list-members'],
    ['workspaces'],
    ['space-overview'], // Space Overview "Members" KPI + members summary
  ],

  // ── Real-time coverage additions ────────────────────────────────
  // Resources the backend now emits via emitDataChanged but that this
  // map previously had no entry for (so events were silently dropped at
  // the `if (!keys) return` guard). Query-key prefixes verified against
  // the pages that consume each resource.
  'work-role': [['work-roles'], ['members']],
  // The two dashboard keys are here because renaming, recolouring or adding a
  // state changes what the Home dashboard's "Tasks by Status" donut and legend
  // render — the backend pads the legend from the live workflow, so a state
  // with no tasks at all still moves that card. Without them a Manager watching
  // the dashboard kept seeing the previous status names until they reloaded the
  // page, while their task lists updated instantly (QA 2026-09-12).
  workflow: [
    ['workflows'],
    ['tasks'],
    ['task-monitor'],
    ['my-tasks'],
    // ['team'] not ['team','tasks'] — React Query prefix-matches, and the
    // Team Overview roster keys on ['team', period] ('weekly' / 'monthly').
    // ['team','tasks'] does NOT prefix-match that, so the roster's Open /
    // Overdue / Done columns sat up to a minute stale on its own 60s poll
    // while every other task surface updated instantly (QA 2026-09-12).
    // The broader prefix covers both team query families, which is right:
    // they read the same task data.
    ['team'],
    ['workspaces'],
    ['board'],
    ['calendar'],
    ['workspace-overview'],
    ['me-dashboard-overview'],
    // Company Admin / Sub Admin "Tasks by Status" card — its legend and
    // colours come from the live workflows.
    ['admin-overview'],
  ],
  label: [['labels'], ['tasks'], ['task-monitor'], ['my-tasks'], ['team', 'tasks'], ['board'], ['calendar']],
  'time-entry': [['time-entries']],
  project: [['projects'], ['project'], ['lists'], ['list'], ['spaces'], ['space']],
  sprint: [['sprints'], ['tasks'], ['board'], ['calendar']],
  'recurring-rule': [['tasks'], ['task-monitor'], ['admin', 'tasks', 'monitor'], ['my-tasks'], ['team', 'tasks'], ['board'], ['calendar']],
  template: [['templates'], ['tasks'], ['lists'], ['list'], ['board'], ['calendar']],
  department: [['departments'], ['members']],
  user: [['members'], ['platform-users'], ['user-memberships'], ['workspace-members'], ['departments']],
  // New company registers (self-signup / Google SSO / Super-Admin create)
  // → refresh the Super Admin Companies list. Emitted to the platform room
  // via `emitPlatformDataChanged` (Super Admins have no tenant room, so a
  // tenant-scoped emit would never reach them — that was the "new company
  // doesn't show until refresh" bug).
  tenant: [['platform-tenants']],
  // Plans — Platform-Admin-managed but consumed by every tenant's Billing
  // page, so the backend broadcasts plan changes globally
  // (emitGlobalDataChanged). Covers the Platform Admin Plans list + dropdown
  // and each company's public Billing catalogue.
  plan: [['platform-plans'], ['platform-plans-dropdown'], ['billing-plans']],
  'workspace-member': [['workspace-members'], ['members'], ['space-members'], ['list-members'], ['departments']],
  'task-attachment': [['task-attachments'], ['files'], ['external-links'], ['space-overview']],
  'task-watcher': [['task-watchers'], ['me', 'watching']],

  // ── Platform Admin / config modules ─────────────────────────────────
  // Wired into the existing bus so admin pages stop showing stale data.
  // Coupons / email templates / platform settings are Platform-Admin-only
  // (backend uses emitPlatformDataChanged → the super-admin room).
  coupon: [['platform-coupons']],
  'email-template': [['email-templates']],
  'platform-settings': [['platform-settings']],
  // External collaborators — tenant-scoped roster (emitDataChanged per tenant).
  'external-collaborator': [['external-collaborators']],
  // Bring-your-own AI provider keys — tenant-scoped; the prefix covers both
  // ['ai-providers','status'] and ['ai-providers','catalogue'].
  'ai-provider-key': [['ai-providers']],
  // Switching the ACTIVE provider — same page, same query tree; the active
  // flag rides on ['ai-providers','status'].
  'ai-provider-active': [['ai-providers']],
  // Runtime fallback order edits ride on ['ai-providers','fallback-order'].
  'ai-fallback-order': [['ai-providers']],
  // AI Firewall (Platform Admin Settings tab) — the prefix covers the policy
  // form and the ['platform-ai-firewall','log'] table.
  'ai-firewall': [['platform-ai-firewall']],
  // Session settings (Platform Admin Settings tab).
  'session-settings': [['session-settings']],
  // Gamification settings + badge catalogue (Company Admin page). Points
  // rules affect FUTURE awards only, so leaderboard keys stay untouched —
  // live point movement already flows through the `task` entry above.
  'gamification-settings': [['gamification-settings']],
  // Task custom-field definitions (Company Admin Custom Fields page).
  'custom-field': [['custom-fields']],
  // Referral lifecycle — the prefix covers the Company Admin Refer & Earn
  // page (['referrals','me']) and the Platform Admin config / funnel / list.
  referral: [['referrals']],
  // Payments ledger — Platform Admin Payments page (emitPlatformDataChanged).
  transaction: [['transactions']],
  // Billing lifecycle (payment completed / free plan activated) — refreshes
  // the paying tenant's invoices + plan catalogue. The current-plan banner is
  // read from the auth store (/auth/me), not React Query, so it isn't listed.
  subscription: [['billing-invoices'], ['billing-plans']],
  // Leads (marketing pipeline) — Platform Admin. Public website submissions
  // emit too, so the SA Leads page updates the moment a lead comes in.
  lead: [['platform-leads']],
  // Email assignments + SMTP settings + payment-gateway settings — Platform Admin.
  'email-assignment': [['email-assignments']],
  'email-settings': [['email-settings']],
  'smtp-account': [['smtp-accounts']],
  'payment-settings': [['payment-settings']],
  // Announcements history — Platform Admin. Tenant bells already refresh via
  // the notification:new path, so only the SA history page is mapped here.
  announcement: [['platform-announcements']],
  // IP access rules — Platform Admin. Enforcement is server-side middleware;
  // this only keeps another admin's settings form fresh.
  'ip-access': [['platform-ip-access']],
  // SOP integration — the tenant Integrations page (integration) + the Platform
  // Admin per-tenant SOP config page (sop-config). A super-admin edit emits both.
  integration: [['integrations']],
  'sop-config': [['super-admin', 'sop-config']],
  // Visitor-log retention setting — Platform Admin. Raw log ingestion is NOT
  // wired (too high-volume for the bus).
  'visitor-retention': [['visitor-logs', 'retention']],
}

// Separate, simpler maps for the legacy server-pushed events that
// pre-date the unified `data:changed` channel. Notifications and
// invitations have their own dedicated emit paths on the backend
// (`emitToUser`) — we re-route them through the same invalidator so
// the Inbox page refreshes live in addition to the bell components.
const NOTIFICATION_KEYS = [
  ['notifications'], // covers ['notifications', 'me', 'inbox' | 'sent']
]
const INVITATION_KEYS = [
  ['invitations'], // covers ['invitations', 'me']
  ['workspaces'],  // accepting an invite shifts workspace membership too
]

export function RealtimeInvalidator() {
  const qc = useQueryClient()
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)

  useEffect(() => {
    // No user → no socket → no subscription. The auth store also
    // disconnects the socket on sign-out, which clears any pending
    // listener as a side effect; this guard is defence-in-depth.
    if (!currentUserId) return undefined

    function invalidateAll(keys) {
      for (const key of keys) {
        // Prefix-match invalidation — `['notifications']` covers
        // every query whose key starts with `['notifications', …]`.
        void qc.invalidateQueries({ queryKey: key })
      }
    }

    // Optimistic delete: remove the row with `id` from every cached
    // list query that matches `keys`, BEFORE the network refetch
    // returns. Without this, the deleted row stays visible for the
    // ~150 ms a refetch takes — which the user perceives as a
    // "blink" when the row briefly reappears in their sidebar /
    // table after the manager deletes it.
    //
    // Handles both shapes used across the app:
    //   - `{ data: [{ id, … }] }` (most list endpoints)
    //   - bare `[{ id, … }]`     (a few helpers)
    // Other shapes (singular `{ id, … }`) are skipped — those are
    // handled by the page's own listQuery.isError → navigate
    // pattern, not by surgical cache patching.
    function optimisticallyRemove(keys, id) {
      if (!id) return
      for (const key of keys) {
        qc.setQueriesData({ queryKey: key }, (old) => {
          if (!old) return old
          if (Array.isArray(old?.data)) {
            const filtered = old.data.filter((r) => r?.id !== id)
            if (filtered.length === old.data.length) return old
            return { ...old, data: filtered }
          }
          if (Array.isArray(old)) {
            const filtered = old.filter((r) => r?.id !== id)
            if (filtered.length === old.length) return old
            return filtered
          }
          return old
        })
      }
    }

    // Debug toggle. Run `localStorage.setItem('realtime.debug','1')` in
    // the browser console and reload — every incoming socket event
    // then logs to the console. Useful for "why isn't tab B
    // refreshing?" investigations; off by default to keep prod quiet.
    const debug =
      typeof window !== 'undefined' &&
      window.localStorage?.getItem('realtime.debug') === '1'
    if (debug) {
      // eslint-disable-next-line no-console
      console.debug('[realtime] listener mounted', { currentUserId })
    }

    // ── Unified data:changed channel ────────────────────────────────
    //
    // No actor-based deduping. We used to skip events whose actorId
    // matched the current user, on the assumption that the mutation's
    // own onSuccess had already invalidated. That broke multi-tab /
    // multi-device sync: if the same user is signed in on tabs A and
    // B, an edit in A would emit with actorId = user.id, and tab B
    // would silently drop it.
    //
    // The cost of NOT deduping is one extra invalidateQueries call
    // on the originating tab. React Query coalesces concurrent
    // refetches of the same key, so in practice it's free — the
    // mutation's onSuccess refetch and the socket's invalidation
    // share one in-flight HTTP request.
    //
    // Important: no resource is hard-reloaded here. An earlier
    // version of this file kept a `HARD_RELOAD_RESOURCES = ['space']`
    // escape hatch that called `window.location.reload()` whenever
    // another user edited a Space. That was destructive: any unsaved
    // state — open modals, in-progress comments, edited documents,
    // half-filled forms — was lost the moment a manager renamed or
    // reordered ANY space anywhere in the tenant. Soft React Query
    // invalidation is the only mechanism we use now. Pages that need
    // to recover from "the resource I'm viewing was deleted" (e.g.
    // SpacePage's `spaceQuery.isError → navigate('/app')` effect)
    // handle that LOCALLY by reacting to the refetch returning 404,
    // not by punting the whole tab.
    const offData = onSocketEvent('data:changed', (payload) => {
      if (debug) {
        // eslint-disable-next-line no-console
        console.debug('[realtime] data:changed', payload)
      }
      if (!payload?.resource) return
      const keys = RESOURCE_KEYS[payload.resource]
      if (!keys) {
        if (debug) {
          // eslint-disable-next-line no-console
          console.warn(
            '[realtime] no key map for resource:',
            payload.resource,
          )
        }
        return
      }
      // Surgical delete first — flushes the row from the cache so
      // the user doesn't see it linger while the refetch is in
      // flight. Falls through to invalidate for safety (covers
      // any list query the optimistic path missed, e.g. ones
      // whose shape this helper doesn't recognise).
      if (payload.op === 'deleted' && payload.id) {
        optimisticallyRemove(keys, payload.id)
      }
      invalidateAll(keys)

      // A Work Role edit changes what the viewer may DO, not just what a
      // page lists. Every permission gate for a `user` (Manager / Team
      // Leader) reads `user.operations` off the AUTH STORE (/auth/me) — not
      // React Query — so invalidating query keys alone left the two halves
      // of the UI disagreeing: page content refetched with the new rules
      // while the sidebar, section links and route guards kept the old
      // ones, until a hard reload. That mismatch is the "glitch" an admin
      // sees when they edit Space operations and watch a Manager's panel.
      //
      // Only refresh when the edited role is the viewer's own, so changing
      // an unrelated role doesn't make every connected client re-fetch
      // /auth/me. `refreshUser` re-normalises through `buildUser`, so the
      // operations array is replaced wholesale rather than merged.
      if (
        payload.resource === 'work-role' &&
        payload.id &&
        useAuthStore.getState().user?.workRoleId &&
        String(payload.id) ===
          String(useAuthStore.getState().user.workRoleId)
      ) {
        void useAuthStore.getState().refreshUser?.()
      }
    })

    // ── Legacy per-user events ──────────────────────────────────────
    // The backend pushes these via `emitToUser` (one socket only, no
    // tenant fan-out), so there's no actorId to dedupe against. The
    // bell components already listen separately; routing through here
    // additionally keeps the Inbox page list fresh.
    const offNotif = onSocketEvent('notification:new', () => {
      invalidateAll(NOTIFICATION_KEYS)
    })
    const offInvite = onSocketEvent('invitation:new', () => {
      invalidateAll(INVITATION_KEYS)
    })

    return () => {
      offData()
      offNotif()
      offInvite()
    }
  }, [qc, currentUserId])

  // Renders nothing; this is a behavioural component.
  return null
}
